import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import MusicalService from '../../services/musicalService';

const NewMusicalModal = ({ isOpen, onClose, onSuccess, prefilledTitle = '' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    title: prefilledTitle || '',
    composer: '',
    lyricist: '',
    book_writer: '',
    year_premiered: '',
    genre: 'broadway',
    description: ''
  });

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.title?.trim()) {
      setError('Musical title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await MusicalService?.createMusical({
        ...formData,
        title: formData?.title?.trim(),
        composer: formData?.composer?.trim() || null,
        lyricist: formData?.lyricist?.trim() || null,
        book_writer: formData?.book_writer?.trim() || null,
        year_premiered: formData?.year_premiered ? parseInt(formData?.year_premiered, 10) : null,
        description: formData?.description?.trim() || null,
        is_active: true,
        created_by: null // Will be set by the service if user context is available
      });

      if (error) {
        setError(error?.message || 'Failed to create musical');
      } else {
        console.log('✅ New musical created:', data);
        onSuccess?.(data);
      }
    } catch (err) {
      console.error('❌ Error creating musical:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white border-opacity-20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
              <Icon name="Plus" size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Create New Musical</h2>
              <p className="text-sm text-blue-100">Add a new musical to the database</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-blue-100" />
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-6 border-b border-white border-opacity-20">
            <div className="flex items-center space-x-3 p-4 bg-red-600 bg-opacity-20 border border-red-400 border-opacity-30 rounded-lg">
              <Icon name="AlertTriangle" size={20} className="text-red-400" />
              <div>
                <p className="font-medium text-red-100">Error</p>
                <p className="text-sm text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Musical Title - Required */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Musical Title *
            </label>
            <Input
              type="text"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              placeholder="Enter musical title"
              required
              disabled={loading}
              className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
            />
          </div>

          {/* Composer and Lyricist */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Composer
              </label>
              <Input
                type="text"
                value={formData?.composer}
                onChange={(e) => handleInputChange('composer', e?.target?.value)}
                placeholder="Composer name"
                disabled={loading}
                className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Lyricist
              </label>
              <Input
                type="text"
                value={formData?.lyricist}
                onChange={(e) => handleInputChange('lyricist', e?.target?.value)}
                placeholder="Lyricist name"
                disabled={loading}
                className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
              />
            </div>
          </div>

          {/* Book Writer and Year */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Book Writer
              </label>
              <Input
                type="text"
                value={formData?.book_writer}
                onChange={(e) => handleInputChange('book_writer', e?.target?.value)}
                placeholder="Book writer name"
                disabled={loading}
                className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Year Premiered
              </label>
              <Input
                type="number"
                value={formData?.year_premiered}
                onChange={(e) => handleInputChange('year_premiered', e?.target?.value)}
                placeholder="e.g., 2019"
                disabled={loading}
                min="1800"
                max="2030"
                className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder:text-blue-200"
              />
            </div>
          </div>

          {/* Genre */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Genre
            </label>
            <Select
              value={formData?.genre}
              onChange={(value) => handleInputChange('genre', value)}
              disabled={loading}
              className="w-full bg-white bg-opacity-10 border-white border-opacity-30 text-white"
            >
              <option value="broadway" className="bg-gray-800">Broadway</option>
              <option value="off_broadway" className="bg-gray-800">Off-Broadway</option>
              <option value="community" className="bg-gray-800">Community Theater</option>
              <option value="school" className="bg-gray-800">School Production</option>
              <option value="opera" className="bg-gray-800">Opera</option>
              <option value="operetta" className="bg-gray-800">Operetta</option>
              <option value="jukebox" className="bg-gray-800">Jukebox Musical</option>
              <option value="rock" className="bg-gray-800">Rock Musical</option>
              <option value="pop" className="bg-gray-800">Pop Musical</option>
              <option value="classical" className="bg-gray-800">Classical</option>
            </Select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              placeholder="Brief description of the musical"
              disabled={loading}
              rows={3}
              className="w-full px-3 py-2 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-white placeholder:text-blue-200"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-white border-opacity-20">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="border-white border-opacity-30 text-blue-100 hover:bg-white hover:bg-opacity-20"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              iconName={loading ? "Loader2" : "Plus"}
              iconPosition="left"
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0"
            >
              {loading ? 'Creating...' : 'Create Musical'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewMusicalModal;